import { useEffect, useState } from "react";
import { sitesApi } from "../../services/sitesApi";
import { useToast } from "../../context/ToastContext";
import { Button } from "../common/Button";
import { Modal } from "../common/Modal";

/**
 * Set or correct a site's coordinates (#204).
 *
 * Until this existed a site's location could only be set once, while
 * creating it in mobile's Add Hive flow. If the GPS fix failed the site was
 * unmappable forever, which is exactly what happened to two sites in dev.
 *
 * Reads the site's current coordinates on open rather than taking them as
 * props: the caller (a hive page) knows site_id but not the site's
 * position, and fetching here keeps the dialog self-contained.
 */
export function SiteLocationDialog({ siteId, siteName, open, onClose, onSaved }) {
  const { notify } = useToast();
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hadLocation, setHadLocation] = useState(false);
  const [address, setAddress] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  // What the address resolved to, held for the user to confirm or reject
  // before it becomes the saved location.
  const [match, setMatch] = useState(null);

  useEffect(() => {
    if (!open || !siteId) return;
    setLoading(true);
    sitesApi
      .list()
      .then((r) => {
        const site = (r.sites || []).find((s) => s.site_id === siteId);
        const has = site?.latitude != null && site?.longitude != null;
        setHadLocation(has);
        setLat(has ? String(site.latitude) : "");
        setLon(has ? String(site.longitude) : "");
        // Pre-fill the address the site already has. A site saved with an
        // address but no coordinates is exactly the case this fixes -- the
        // user should be one click from resolving it, not re-typing it.
        setAddress(site?.address || "");
        setMatch(null);
      })
      .catch(() => notify("Could not load the site's current location", "danger"))
      .finally(() => setLoading(false));
  }, [open, siteId, notify]);

  function useMyLocation() {
    if (!navigator.geolocation) {
      notify("This browser can't provide a location", "warning");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Six decimals is ~0.1 m. More is false precision from a phone GPS
        // and just makes the field harder to read.
        setLat(pos.coords.latitude.toFixed(6));
        setLon(pos.coords.longitude.toFixed(6));
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        notify(
          err.code === err.PERMISSION_DENIED
            ? "Location permission was denied — you can still type the coordinates"
            : "Couldn't get your location — you can still type the coordinates",
          "warning",
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  }

  async function lookUpAddress() {
    if (!address.trim()) {
      notify("Enter an address to look up", "warning");
      return;
    }
    setLookingUp(true);
    setMatch(null);
    try {
      const r = await sitesApi.geocode(address.trim());
      // Held for confirmation, NOT written into the coordinate fields yet.
      // A geocoder answers confidently even when it has guessed, and the
      // thing being placed is a beehive — nobody finds out it is in the
      // wrong county until they drive there.
      setMatch(r);
    } catch (err) {
      notify(err.apiMessage || "Could not look up that address", "danger");
    } finally {
      setLookingUp(false);
    }
  }

  function acceptMatch() {
    setLat(String(match.latitude));
    setLon(String(match.longitude));
    setMatch(null);
  }

  async function save() {
    const hasLat = lat.trim() !== "";
    const hasLon = lon.trim() !== "";
    // The API rejects half a coordinate, but saying so here beats a round
    // trip to be told the same thing.
    if (hasLat !== hasLon) {
      notify("Enter both latitude and longitude, or clear both", "warning");
      return;
    }
    setSaving(true);
    try {
      await sitesApi.update(siteId, {
        latitude: hasLat ? Number(lat) : null,
        longitude: hasLon ? Number(lon) : null,
        // Persist the address too. If the user corrected it to get a good
        // match, that correction is worth keeping -- and it means the site
        // can be re-geocoded later without anyone retyping it.
        address: address.trim() || null,
      });
      notify(hasLat ? "Site location saved" : "Site location cleared", "success");
      onSaved?.();
      onClose();
    } catch (err) {
      notify(err.apiMessage || "Could not save the site location", "danger");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={siteName ? `Location for ${siteName}` : "Site location"}
      footer={
        <Button onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save location"}
        </Button>
      }
    >
      {loading ? (
        <p>Loading…</p>
      ) : (
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {!hadLocation && (
            <p style={{ color: "var(--color-warning)", margin: 0 }}>
              This site has no location yet, so it doesn't appear on any map.
            </p>
          )}

          {/* Address first, deliberately. It needs no browser permission,
              reveals nothing about where you are now, and works from a
              desk nowhere near the apiary. "Use my location" is the faster
              option when you are stood in the yard, so it stays — but it
              should not be the only way in. */}
          <label>
            Address
            <input id="site-location-dialog-address" name="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookUpAddress(); } }}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <Button variant="secondary" onClick={lookUpAddress} disabled={lookingUp}>
            {lookingUp ? "Looking up…" : "Find coordinates from address"}
          </Button>

          {match && (
            <div style={{
              border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)",
              padding: "var(--space-3)", background: "var(--color-bg-subtle)",
            }}>
              <p style={{ margin: "0 0 var(--space-2)", fontWeight: 600 }}>Is this right?</p>
              <p style={{ margin: "0 0 var(--space-1)" }}>{match.formatted_address}</p>
              <p style={{ margin: "0 0 var(--space-2)", color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                {match.latitude}, {match.longitude}
              </p>
              {/* APPROXIMATE usually means it fell back to a town or
                  postcode centroid. For a yard down a county road that can
                  be miles out, and saying so beats implying precision the
                  result does not have. */}
              {(match.location_type === "APPROXIMATE" || match.partial_match) && (
                <p style={{ margin: "0 0 var(--space-2)", color: "var(--color-warning)", fontSize: "var(--font-size-sm)" }}>
                  This is an approximate match — it may be the nearest town rather than the exact spot. Check it on the map after saving.
                </p>
              )}
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <Button size="sm" onClick={acceptMatch}>Use this location</Button>
                <Button size="sm" variant="secondary" onClick={() => setMatch(null)}>No, discard</Button>
              </div>
            </div>
          )}

          <Button variant="secondary" onClick={useMyLocation} disabled={locating}>
            {locating ? "Getting location…" : "Use my location"}
          </Button>

          {/* Real labels, not placeholders -- see #205. These also need to
              be readable when typed into, which is why the inputs inherit
              the global input styling fixed in 955971e. */}
          <label>
            Latitude
            <input id="site-location-dialog-lat" name="lat"
              type="number"
              step="any"
              min={-90}
              max={90}
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label>
            Longitude
            <input id="site-location-dialog-lon" name="lon"
              type="number"
              step="any"
              min={-180}
              max={180}
              value={lon}
              onChange={(e) => setLon(e.target.value)}
              className="touch-target"
              style={{ display: "block", width: "100%" }}
            />
          </label>

          <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", margin: 0 }}>
            Clearing both fields removes the location. A site recorded in the
            wrong place is worse than one with no place at all.
          </p>
        </div>
      )}
    </Modal>
  );
}
